import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useDataProvider, usePermissions } from 'react-admin';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import useDashboardContext from '../../hooks/useDashboardContext';
import { getAttendantId, getChainId, getEmployeeType } from '../../lib';
import { forceTwoDigits } from '../../utils';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Card, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { EmployeeType } from '../../models';

export const FillinsAverageTime = () => {
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const {
    from, to, chain, stationIds,
    timeMetricsData, timeMetricsLoading,
    setTimeMetricsData, setTimeMetricsLoading
  } = useDashboardContext();

  const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

  // Carrega dados do endpoint consolidado (compartilhado com FillinDuration)
  useEffect(() => {
    if (timeMetricsLoading) return;

    const filter = { from, to };
    if (stationIds && stationIds.length > 0) { filter.placeIds = stationIds; }
    if (getEmployeeType() === EmployeeType.attendant) { filter.attendantId = getAttendantId(); }

    setTimeMetricsLoading(true);
    dataProvider.getList('dashboard/fillin-time-metrics', {
      filter, chainId,
    }).then(({ data }) => {
      const metrics = data[0];
      setTimeMetricsData({
        averageTime: metrics?.averageTime || null,
        duration: metrics?.duration || null,
        totalFillins: metrics?.totalFillins || 0,
      });
    }).catch(err => {
      console.error(err);
      setTimeMetricsData(null);
    }).finally(() => {
      setTimeMetricsLoading(false);
    });
  }, [from, to, chain, stationIds]);

  // Deriva o valor formatado do contexto
  const avg = useMemo(() => {
    if (!timeMetricsData?.averageTime) return '--:--:--';

    const average = timeMetricsData.averageTime;
    const totalHour = moment.duration('00:00:00').add(average * 60, 'seconds').asHours();
    const hour = Math.floor(totalHour);
    const minutes = Math.floor((totalHour - hour) * 60);
    const seconds = Math.floor((((totalHour - hour) * 60) - minutes) * 60);

    return `${forceTwoDigits(hour)}:${forceTwoDigits(minutes)}:${forceTwoDigits(seconds)}`;
  }, [timeMetricsData]);

  return (
    <Permission permission={PERMISSIONS.FILLINS_AVERAGE_TIME}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          '& a': {
            textDecoration: 'none',
            color: 'inherit',
          },
        }}
      >
        <Link to="/your-link">
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '& .icon': {
                color: 'secondary.main',
              },
              '&:before': {
                position: 'absolute',
                top: '50%',
                left: 0,
                display: 'block',
                content: `''`,
                height: '200%',
                aspectRatio: '1',
                transform: 'translate(-30%, -60%)',
                borderRadius: '50%',
                backgroundColor: 'secondary.main',
                opacity: 0.15,
              },
            }}
          >
            <Box width="3em" className="icon">
              <AccessTimeIcon fontSize="large" />
            </Box>
            <Box textAlign="right">
              <Typography color="textSecondary">Um abastecimento a cada</Typography>
              <Typography variant="h5" component="h2">
                {avg}
              </Typography>
            </Box>
          </Box>
        </Link>
      </Card>
    </Permission>
  );
};
